import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://sohaibenterprises.cloud/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px', display: 'block' };

const SettingsTab = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'businessman';

  // Outer tab: 'categories' or 'crops'
  const [mainTab, setMainTab] = useState('categories');
  // Inside Categories: 'expense_category' or 'income_category'
  const [categoryType, setCategoryType] = useState('expense_category');

  const [lists, setLists] = useState({
    expense_category: [],
    income_category: [],
    crop_name: [],
    crop_type: []
  });
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [modalContext, setModalContext] = useState(null); // { type, parentId, parentName }
  const [editId, setEditId] = useState(null);
  const [formValue, setFormValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [exp, inc, crops, cropTypes] = await Promise.all([
        axios.get(`${API}/agriculture/settings/expense_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/income_category`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/crop_name`, { headers: headers() }),
        axios.get(`${API}/agriculture/settings/crop_type`, { headers: headers() })
      ]);
      setLists({
        expense_category: exp.data.data || [],
        income_category: inc.data.data || [],
        crop_name: crops.data.data || [],
        crop_type: cropTypes.data.data || []
      });
    } catch (err) {
      console.error('Failed to load settings', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // Open modal to ADD a category (expense or income)
  const openAddCategory = () => {
    setModalContext({ type: categoryType, parentId: null, parentName: null });
    setEditId(null);
    setFormValue('');
    setError('');
    setShowForm(true);
  };

  // Open modal to ADD a crop name
  const openAddCrop = () => {
    setModalContext({ type: 'crop_name', parentId: null, parentName: null });
    setEditId(null);
    setFormValue('');
    setError('');
    setShowForm(true);
  };

  // Open modal to ADD a crop type under a specific crop
  const openAddType = (crop) => {
    setModalContext({ type: 'crop_type', parentId: crop.id, parentName: crop.value });
    setEditId(null);
    setFormValue('');
    setError('');
    setShowForm(true);
  };

  // Open modal to EDIT any item
  const openEdit = (item) => {
    setModalContext({ type: item.setting_type, parentId: item.parent_id, parentName: null });
    setEditId(item.id);
    setFormValue(item.value);
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    const trimmed = formValue.trim();
    if (!trimmed) { setError('Name cannot be empty'); return; }
    if (trimmed.length > 100) { setError('Name must be 100 characters or less'); return; }

    setSaving(true);
    setError('');
    try {
      if (editId) {
        await axios.put(`${API}/agriculture/settings/${editId}`, { value: trimmed }, { headers: headers() });
      } else {
        const payload = { setting_type: modalContext.type, value: trimmed };
        if (modalContext.type === 'crop_type') payload.parent_id = modalContext.parentId;
        await axios.post(`${API}/agriculture/settings`, payload, { headers: headers() });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save';
      setError(msg);
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    const cascadeWarning = item.setting_type === 'crop_name'
      ? `\n\n⚠️ This will also delete all crop types under "${item.value}".`
      : '';
    if (!window.confirm(`Delete "${item.value}"?${cascadeWarning}`)) return;
    try {
      await axios.delete(`${API}/agriculture/settings/${item.id}`, { headers: headers() });
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete';
      alert(msg);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>Loading settings...</div>;

  // Helpers for current category list
  const categoryList = lists[categoryType];
  const categoryColor = categoryType === 'expense_category' ? '#e74c3c' : '#27ae60';

  // Types grouped by parent crop id
  const typesByCrop = (() => {
    const map = {};
    lists.crop_type.forEach(t => {
      if (!map[t.parent_id]) map[t.parent_id] = [];
      map[t.parent_id].push(t);
    });
    return map;
  })();

  // Modal title + placeholder based on context
  const modalTitle = (() => {
    if (!modalContext) return '';
    const action = editId ? 'Edit' : 'Add';
    const labels = {
      expense_category: 'Expense Category',
      income_category: 'Income Category',
      crop_name: 'Crop',
      crop_type: `Crop Type${modalContext.parentName ? ` for ${modalContext.parentName}` : ''}`
    };
    return `${action} ${labels[modalContext.type]}`;
  })();

  const modalPlaceholder = (() => {
    if (!modalContext) return '';
    return {
      expense_category: 'e.g. Diesel, Tractor Rent',
      income_category: 'e.g. Subsidy, Grant',
      crop_name: 'e.g. Wheat, Sugarcane',
      crop_type: 'e.g. Barani, Hybrid'
    }[modalContext.type];
  })();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 }}>⚙️ Settings</h2>
          <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>
            Manage dropdown options used throughout the agriculture module
          </div>
        </div>
        {isOwner && (
          <button
            onClick={mainTab === 'categories' ? openAddCategory : openAddCrop}
            style={{ padding: '12px 24px', background: mainTab === 'categories' ? categoryColor : '#16a085', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
          >
            {mainTab === 'categories'
              ? `+ Add ${categoryType === 'expense_category' ? 'Expense' : 'Income'} Category`
              : '+ Add Crop'}
          </button>
        )}
      </div>

      {!isOwner && (
        <div style={{ padding: '14px 18px', background: '#fff3cd', borderRadius: '10px', borderLeft: '4px solid #f39c12', fontSize: '13px', color: '#7f5b00', marginBottom: '20px' }}>
          🔒 You are logged in as <strong>{user?.role}</strong>. Only the owner can add, edit, or delete settings.
        </div>
      )}

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setMainTab('categories')}
          style={{
            padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
            background: mainTab === 'categories' ? '#3498db' : '#f0f0f0',
            color: mainTab === 'categories' ? 'white' : '#555'
          }}
        >
          💰 Categories ({lists.expense_category.length + lists.income_category.length})
        </button>
        <button
          onClick={() => setMainTab('crops')}
          style={{
            padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
            background: mainTab === 'crops' ? '#16a085' : '#f0f0f0',
            color: mainTab === 'crops' ? 'white' : '#555'
          }}
        >
          🌾 Crops ({lists.crop_name.length})
        </button>
      </div>

      {/* ─── CATEGORIES TAB ─────────────────────────────── */}
      {mainTab === 'categories' && (
        <div>
          {/* Expense / Income switch */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
            <button
              onClick={() => setCategoryType('expense_category')}
              style={{
                padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                background: categoryType === 'expense_category' ? '#e74c3c' : '#f0f0f0',
                color: categoryType === 'expense_category' ? 'white' : '#555'
              }}
            >
              📉 Expense ({lists.expense_category.length})
            </button>
            <button
              onClick={() => setCategoryType('income_category')}
              style={{
                padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                background: categoryType === 'income_category' ? '#27ae60' : '#f0f0f0',
                color: categoryType === 'income_category' ? 'white' : '#555'
              }}
            >
              💰 Income ({lists.income_category.length})
            </button>
          </div>

          {/* Category table */}
          <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: categoryColor, margin: 0 }}>
                {categoryType === 'expense_category' ? '📉 Expense Categories' : '💰 Income Categories'}
              </h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  {['#', 'Name', 'Order', isOwner ? 'Actions' : null].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#555', borderBottom: '1px solid #eee' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categoryList.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>No entries yet. Click "+ Add" to create one.</td></tr>
                ) : categoryList.map((item, i) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', color: '#7f8c8d' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2c3e50' }}>{item.value}</td>
                    <td style={{ padding: '12px 16px', color: '#555' }}>{item.display_order}</td>
                    {isOwner && (
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => openEdit(item)} style={{ padding: '6px 14px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                          <button onClick={() => handleDelete(item)} style={{ padding: '6px 14px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── CROPS TAB (cards with nested types) ─────────── */}
      {mainTab === 'crops' && (
        <div>
          {lists.crop_name.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
              No crops yet. Click "+ Add Crop" to create your first crop.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {lists.crop_name.map(crop => {
                const types = typesByCrop[crop.id] || [];
                return (
                  <div key={crop.id} style={{ background: 'white', borderRadius: '14px', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    {/* Crop header */}
                    <div style={{ padding: '16px 18px', background: 'linear-gradient(135deg, #16a085 0%, #1abc9c 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>🌾 {crop.value}</div>
                      {isOwner && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(crop)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                          <button onClick={() => handleDelete(crop)} style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </div>
                      )}
                    </div>
                    {/* Types list */}
                    <div style={{ padding: '14px 18px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#95a5a6', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Crop Types ({types.length})
                      </div>
                      {types.length === 0 ? (
                        <div style={{ fontSize: '13px', color: '#bbb', fontStyle: 'italic', marginBottom: '12px' }}>No types added yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {types.map(t => (
                            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                              <span style={{ fontSize: '14px', color: '#2c3e50', fontWeight: '500' }}>🏷️ {t.value}</span>
                              {isOwner && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={() => openEdit(t)} style={{ padding: '3px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                                  <button onClick={() => handleDelete(t)} style={{ padding: '3px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {isOwner && (
                        <button onClick={() => openAddType(crop)} style={{ width: '100%', padding: '10px', background: '#e8f6f3', color: '#16a085', border: '1px dashed #16a085', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                          + Add Type
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Info note */}
      <div style={{ marginTop: '20px', padding: '14px 18px', background: '#e8f4fd', borderRadius: '10px', borderLeft: '4px solid #3498db', fontSize: '13px', color: '#1a5490' }}>
        💡 <strong>Note:</strong> Expense/Income categories used in existing transactions cannot be deleted. Crop names and types can be deleted freely — old crop records keep their existing names unchanged.
      </div>

      {/* Add/Edit Modal */}
      {showForm && modalContext && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>{modalTitle}</h3>
            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '20px' }}>
              {editId ? 'Update the name below.' : 'This will be added to the dropdown.'}
            </div>
            <div>
              <label style={labelStyle}>Name *</label>
              <input
                style={inputStyle}
                value={formValue}
                onChange={e => setFormValue(e.target.value)}
                placeholder={modalPlaceholder}
                autoFocus
                maxLength={100}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              />
            </div>
            {error && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fdecea', borderRadius: '8px', color: '#c0392b', fontSize: '13px' }}>
                ❌ {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '12px 24px', background: '#f0f0f0', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '12px 24px', background: '#16a085', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                {saving ? 'Saving...' : editId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;